// A thin Console-facing client. Wraps window.atlas (the SDK) and adds the few control-plane calls
// the Console needs that the SDK doesn't expose (catalog, ARQ review). Everything the views use
// goes through this object, so tests inject a fake client instead of a browser + window.atlas.

import type {
  ActionInfo,
  Atlas,
  Client,
  DeployInput,
  DeployOptions,
  DeployResult,
  FetchImpl,
  Identity,
} from "./types";

export function makeClient(atlas: Atlas, fetchImpl: FetchImpl): Client {
  // atlas: window.atlas (SDK); fetchImpl: an authed fetch (token attached). In the browser we
  // build fetchImpl from atlas.auth.getIdToken + the configured API endpoint.
  return {
    auth: atlas.auth,
    preferences: atlas.preferences,
    workflow: atlas.workflow,
    // The custom-domain apex (e.g. "dev.tryorion.xyz"), so the catalog can build subdomain links
    // for apps whose routingMode is "subdomain". Empty when not on a custom domain (path mode only).
    appDomain: (atlas.config && atlas.config.appDomain) || "",

    // The signed-in user's role/identity from the ID token, for role-aware UI (e.g. hiding the
    // admin-only Review queue). { sub, groups, isAdmin }. The API still enforces authorization
    // server-side, so hiding a tab is never the security boundary. Returns null when logged out.
    getIdentity(): Identity | null {
      const claims = atlas.auth.getClaims && atlas.auth.getClaims();
      if (!claims) return null;
      const raw = claims["cognito:groups"];
      const groups = Array.isArray(raw) ? (raw as string[]) : [];
      return { sub: (claims.sub as string) || "", groups, isAdmin: groups.includes("atlas-admins") };
    },

    // Catalog: GET /apps (added to registry_api for the Console).
    async listApps() {
      const res = await fetchImpl("GET", "/apps");
      return res.apps || [];
    },

    // Action catalog: GET /actions, every action vended by a live app.
    async listActions(): Promise<ActionInfo[]> {
      const res = await fetchImpl("GET", "/actions");
      return res.actions || [];
    },

    // One app's detail: GET /apps/{app}.
    getAppDetail(appName) {
      return fetchImpl("GET", `/apps/${enc(appName)}`);
    },

    // Roll the live version back to a prior approved one with POST /apps/{app}/rollback.
    // Atomic pointer switch on the backend; no re-upload, no new version.
    rollbackApp(appName, version) {
      return fetchImpl("POST", `/apps/${enc(appName)}/rollback`, { version });
    },

    // App config: the bundle's config.json.
    getAppConfig(appName) {
      return fetchImpl("GET", `/apps/${enc(appName)}/config`);
    },
    saveAppConfig(appName, config) {
      return fetchImpl("POST", `/apps/${enc(appName)}/config`, { config });
    },

    // ARQ review (atlas-admins only). The SDK does not wrap these endpoints.
    async listPendingApprovals() {
      const res = await fetchImpl("GET", "/arq");
      return res.pending || [];
    },
    approveApp(appName, appVersion) {
      return fetchImpl("POST", `/arq/${enc(appName)}/${enc(appVersion)}/approve`);
    },
    rejectApp(appName, appVersion, comments) {
      return fetchImpl("POST", `/arq/${enc(appName)}/${enc(appVersion)}/reject`, { comments });
    },

    // Deploy an app from the browser by uploading a zip bundle. Mirrors the CLI's 4-step flow:
    //   1) GET /upload-location  -> presigned S3 PUT URL + key   (authed)
    //   2) PUT the zip bytes to that URL without auth headers (presigned; see putRaw)
    //   3) POST /apps {name, version, assetsLocation, manifest}  (authed; ownership enforced here)
    //   4) poll GET /deploy-status/{id} until terminal           (authed)
    // `bytes` is the raw zip (Uint8Array/ArrayBuffer/Blob); name/version/manifest come from the
    // zip's package.json (parsed by the caller via readZipManifest). onStep(msg) reports progress.
    async deploy(
      { name, version, manifest, bytes }: DeployInput,
      { onStep, pollMs = 2000, maxPolls = 100 }: DeployOptions = {},
    ): Promise<DeployResult> {
      const step = (m: string) => onStep && onStep(m);
      step("requesting upload location…");
      const loc = await fetchImpl("GET", "/upload-location");
      step(`uploading ${sizeOf(bytes)} bytes…`);
      await putRaw(loc.preSignedUrl, bytes);
      step("registering version…");
      const reg = await fetchImpl("POST", "/apps", {
        name,
        version,
        assetsLocation: loc.key,
        manifest: manifest || {},
      });
      const workflowId = reg.workflowId;
      step("waiting for the registration pipeline…");
      for (let i = 0; i < maxPolls; i++) {
        const { status } = await fetchImpl("GET", `/deploy-status/${enc(workflowId)}`);
        if (status === "SUCCEEDED") return { status, name, version };
        if (["FAILED", "TIMED_OUT", "ABORTED"].includes(status)) {
          throw new Error(`deploy ${String(status).toLowerCase()}`);
        }
        await sleep(pollMs);
      }
      throw new Error("deploy timed out waiting for the pipeline");
    },
  };
}

function sizeOf(bytes: Uint8Array | ArrayBuffer | Blob): number {
  if (bytes instanceof Blob) return bytes.size;
  if (bytes instanceof ArrayBuffer) return bytes.byteLength;
  return bytes.length ?? 0;
}

function sleep(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms));
}

// A minimal Response-like shape so putRaw is testable with an injected fetch.
type PutResponse = { ok: boolean; status: number };
type PutFetch = (url: string, init: { method: string; body: BodyInit }) => Promise<PutResponse>;

// Raw presigned-S3 PUT: NO Authorization header (it breaks the presigned SigV4 signature) and the
// body is the raw bytes (never JSON-stringified). Deliberately separate from makeAuthedFetch, which
// always attaches the token + content-type. Injectable for tests via the 3rd arg.
export async function putRaw(
  url: string,
  bytes: Uint8Array | ArrayBuffer | Blob,
  fetchFn: PutFetch = fetch as unknown as PutFetch,
): Promise<void> {
  const resp = await fetchFn(url, { method: "PUT", body: bytes as BodyInit });
  if (!resp.ok) throw new Error(`upload failed: HTTP ${resp.status}`);
}

function enc(s: string): string {
  return encodeURIComponent(s);
}

// Build an authed fetch from the SDK auth + API endpoint (browser only).
export function makeAuthedFetch(atlas: Atlas): FetchImpl {
  const api = atlas.config!.apiEndpoint;
  return async (method: string, path: string, body?: unknown) => {
    const token = await atlas.auth.getIdToken();
    // No cached session: kick off login rather than sending `authorization: undefined` and
    // surfacing an opaque 401. login() returns after redirecting; throw so the caller stops.
    if (!token) {
      atlas.auth.login();
      throw new Error("not authenticated; redirecting to login");
    }
    const resp = await fetch(api + path, {
      method,
      headers: { authorization: token, "content-type": "application/json" },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
    if (!resp.ok) {
      let detail: string;
      try {
        detail = (await resp.json()).message;
      } catch {
        detail = `HTTP ${resp.status}`;
      }
      throw new Error(detail);
    }
    return resp.json();
  };
}
